/**
 * /ratings — full dashboard of every rating the user has received.
 *
 * Layout per the design (app-screens.jsx → RatingsScreenDesktop):
 *   - AppTopbar with "Feedback so far" eyebrow + italic display title.
 *   - Summary: big 92px italic coral %, divider, three SummaryStats
 *     (Would date / Wouldn't / New this week). The 'With notes' count
 *     used to live here too but it's the same number as the filter
 *     pill below — dropped to cut redundancy.
 *   - Filter pills: All / With notes / Would date / Wouldn't, each
 *     showing its count.
 *   - 3-column grid of RatingCard rows.
 *
 * Currently sources data from ratingsApi.dashboard() — that returns a
 * capped `recent[]`. A dedicated paginated /profiles/me/ratings/list
 * endpoint would let us show the full history; for v1 the dashboard
 * payload covers what people actually want (the latest dozen-ish).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ratingsApi, type Period, type RatingsDashboard } from '@/lib/api';
import { AppTopbar, Btn, Eyebrow, Icons } from '@/components';
import { ShareCardModal } from '@/components/ShareCardModal';
import { ShareStatsCardModal } from '@/components/ShareStatsCardModal';
import { useAppContext } from '@/components/AppShell';

type Recent = RatingsDashboard['recent'][number];
type Filter = 'all' | 'notes' | 'yes' | 'no';

export function RatingsPage() {
  const { profile } = useAppContext();
  const [data, setData] = useState<RatingsDashboard | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [sharing, setSharing] = useState<string | null>(null);
  // Time window for the whole screen — stats hero + the recent list
  // both re-render on change. Refetch fires from the useEffect
  // dependency on period.
  const [period, setPeriod] = useState<Period>('all');
  // Whether the stats-card share modal is open. Distinct from
  // `sharing` (which carries the rating-id for the per-comment modal)
  // since the two surfaces have different shapes + endpoints.
  const [statsShareOpen, setStatsShareOpen] = useState(false);

  useEffect(() => {
    // Re-fetches whenever the user flips the period pill. The hero
    // + counts + list all recompute from the new payload.
    ratingsApi.dashboard(period).then(setData).catch(() => null);
  }, [period]);

  const recent = data?.recent ?? [];
  const counts = useMemo(() => ({
    all: recent.length,
    notes: recent.filter((r) => r.note?.trim()).length,
    yes: recent.filter((r) => r.verdict === 'yes').length,
    no: recent.filter((r) => r.verdict === 'no').length,
  }), [recent]);

  // 7-day delta from the recent buffer — same caveat as Home: capped
  // by what `recent` includes. Adequate at v1 scale.
  const deltaWeek = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return recent.filter((r) => new Date(r.created_at).getTime() >= cutoff).length;
  }, [recent]);

  const filtered = useMemo(() => {
    if (filter === 'notes') return recent.filter((r) => r.note?.trim());
    if (filter === 'yes') return recent.filter((r) => r.verdict === 'yes');
    if (filter === 'no') return recent.filter((r) => r.verdict === 'no');
    return recent;
  }, [recent, filter]);

  if (!data) {
    return (
      <>
        <AppTopbar eyebrow="Feedback so far" title={<><span style={{ fontStyle: 'italic' }}>Ratings</span> & notes</>} />
        <div style={{ padding: '0 40px', color: 'var(--ink-3)' }}>Loading…</div>
      </>
    );
  }

  return (
    <>
      <AppTopbar eyebrow="Feedback so far" title={<><span style={{ fontStyle: 'italic' }}>Ratings</span> & notes</>} />

      <div className="ratings-shell" style={{ padding: '0 40px 60px' }}>
        {/* PERIOD PILLS — top of the screen. Active pill flips the
            scope of the entire dashboard (hero + counts + list). */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
          <PeriodPill label="All time"  k="all"   active={period} onChange={setPeriod} />
          <PeriodPill label="Past week" k="week"  active={period} onChange={setPeriod} />
          <PeriodPill label="Today"     k="today" active={period} onChange={setPeriod} />
        </div>

        {/* SUMMARY — bare, sits on the page bg. Same data + grid as
            before; chrome (surface, padding, shadow) removed so the
            big italic % owns the eye line. Mobile collapses the grid
            via .ratings-summary in base.css.
            Share icon overlays the top-right corner of the hero (same
            language as the per-comment Share icon below); opens the
            stats-card modal scoped to the current period. */}
        <div
          className="ratings-summary"
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            alignItems: 'center',
            gap: 40,
            padding: '8px 0',
            position: 'relative',
          }}
        >
          <button
            type="button"
            onClick={() => setStatsShareOpen(true)}
            aria-label="Share my stats"
            title="Share my stats"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 36,
              height: 36,
              borderRadius: 36,
              background: 'var(--surface)',
              color: 'var(--ink)',
              border: '1px solid var(--line)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.share size={15} />
          </button>
          <div>
            <Eyebrow letter={2} style={{ marginBottom: 6 }}>Would date</Eyebrow>
            <div
              className="serif ratings-summary-pct"
              style={{
                fontSize: 92,
                lineHeight: 0.9,
                fontWeight: 500,
                fontStyle: 'italic',
                color: 'var(--coral)',
              }}
            >
              {Math.round(data.yes_percent)}
              <span style={{ fontSize: 32, color: 'var(--ink-3)' }}>%</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 12 }}>
              {data.rating_count} rating{data.rating_count === 1 ? '' : 's'}
              {/* Week-delta badge only makes sense on the all-time
                  view; inside a windowed view (today/week) the meta
                  is either the whole dataset or redundant. */}
              {period === 'all' && deltaWeek > 0 && (
                <>
                  {' · '}
                  <span style={{ color: 'var(--coral)', fontWeight: 600 }}>+{deltaWeek}</span> this week
                </>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <div className="ratings-summary-divider" style={{ borderLeft: '1px solid var(--line)', height: 120 }} />
            <div className="ratings-summary-stats" style={{ flex: 1, display: 'flex', gap: 36, flexWrap: 'wrap' }}>
              <SummaryStat label="Would date" value={data.yes_count} />
              <SummaryStat label="Wouldn't"   value={data.no_count} />
              {/* "New this week" is redundant inside a windowed view
                  (the whole column IS the window). Only render it on
                  the all-time view. */}
              {period === 'all' && (
                <SummaryStat label="New this week" value={`+${deltaWeek}`} accent />
              )}
            </div>
          </div>
        </div>

        {/* FILTER PILLS ──────────────────────────────────────── */}
        <div style={{ marginTop: 28, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <FilterPill label="All"          k="all"   active={filter} count={counts.all}   onChange={setFilter} />
          <FilterPill label="With notes"   k="notes" active={filter} count={counts.notes} onChange={setFilter} />
          <FilterPill label="Would date"   k="yes"   active={filter} count={counts.yes}   onChange={setFilter} />
          <FilterPill label="Wouldn't"     k="no"    active={filter} count={counts.no}    onChange={setFilter} />
        </div>

        {/* GRID ─────────────────────────────────────────────── */}
        {filtered.length === 0 ? (
          data.rating_count === 0 ? (
            // First-run state: no ratings at all. Bare — sits on the
            // page background like every other section on this screen,
            // matching the RN ratings dashboard's empty state.
            <div
              style={{
                marginTop: 32,
                padding: '8px 8px',
                textAlign: 'center',
                color: 'var(--ink-3)',
              }}
            >
              <div className="serif" style={{ fontSize: 24, fontStyle: 'italic', fontWeight: 500, color: 'var(--ink)', marginBottom: 8 }}>
                No ratings yet.
              </div>
              <div style={{ marginBottom: 18 }}>Share your link to get the first one.</div>
              <Link to="/home" style={{ textDecoration: 'none' }}>
                <Btn variant="primary" icon={<Icons.share size={14} />}>Share my profile</Btn>
              </Link>
            </div>
          ) : (
            // Filter-empty state: terse, no card chrome.
            <div style={{ marginTop: 28, padding: '32px 8px', color: 'var(--ink-3)', fontSize: 14, textAlign: 'center' }}>
              Nothing to see here.
            </div>
          )
        ) : (
          <div className="ratings-grid" style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {filtered.map((r) => (
              <RatingCard key={r.id} r={r} onShare={() => setSharing(r.id)} />
            ))}
          </div>
        )}
      </div>

      {sharing && (
        <ShareCardModal
          ratingId={sharing}
          slug={profile?.slug ?? ''}
          onClose={() => setSharing(null)}
        />
      )}

      {/* Stats share modal — scoped to whatever period is active.
          Re-fetches the SVG when period changes via its own useEffect
          (caller doesn't have to remount). */}
      {statsShareOpen && (
        <ShareStatsCardModal
          period={period}
          slug={profile?.slug ?? ''}
          onClose={() => setStatsShareOpen(false)}
        />
      )}
    </>
  );
}

function SummaryStat({ label, value, accent }: { label: string; value: number | string; accent?: boolean }) {
  return (
    <div>
      <div
        className="serif"
        style={{
          fontSize: 30,
          fontWeight: 500,
          fontStyle: 'italic',
          color: accent ? 'var(--coral)' : 'var(--ink)',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <Eyebrow size="sm" letter={1} style={{ marginTop: 6 }}>{label}</Eyebrow>
    </div>
  );
}

// Period pills — same shape as FilterPill but no count badge (the
// period scope is binary per pill, not a filterable count) and a
// distinct type slot so swapping handlers is type-safe.
function PeriodPill({
  label, k, active, onChange,
}: { label: string; k: Period; active: Period; onChange: (v: Period) => void }) {
  const isActive = active === k;
  return (
    <button
      type="button"
      onClick={() => onChange(k)}
      style={{
        padding: '8px 14px',
        borderRadius: 100,
        background: isActive ? 'var(--ink)' : 'var(--chip)',
        color: isActive ? 'var(--bg)' : 'var(--ink)',
        fontSize: 12.5,
        fontWeight: 600,
        border: 'none',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function FilterPill({
  label, k, active, count, onChange,
}: { label: string; k: Filter; active: Filter; count: number; onChange: (v: Filter) => void }) {
  const isActive = active === k;
  return (
    <button
      type="button"
      onClick={() => onChange(k)}
      style={{
        padding: '10px 16px',
        borderRadius: 100,
        background: isActive ? 'var(--ink)' : 'var(--chip)',
        color: isActive ? 'var(--bg)' : 'var(--ink)',
        fontSize: 13,
        fontWeight: 500,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        border: 'none',
        cursor: 'pointer',
      }}
    >
      {label}
      <span
        className="mono"
        style={{ opacity: 0.55, fontSize: 11 }}
      >
        {count}
      </span>
    </button>
  );
}

function RatingCard({ r, onShare }: { r: Recent; onShare: () => void }) {
  const yes = r.verdict === 'yes';
  const hasNote = !!r.note?.trim();
  // No card chrome — sits directly on the page bg. A hairline divider
  // along the top gives each rating a visual edge in the grid; the
  // first row sees the divider too (consistent rhythm > pixel-saving).
  return (
    <div style={{ padding: '18px 4px 6px', borderTop: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 44,
            background: yes ? 'var(--coral)' : 'var(--ink)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {yes ? <Icons.heart size={20} fill="currentColor" /> : <Icons.x size={20} stroke={2.4} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Eyebrow size="sm" letter={1}>Anonymous · {relativeWhen(r.created_at)}</Eyebrow>
          <div
            style={{
              fontSize: 12,
              color: yes ? 'var(--coral-ink)' : 'var(--ink-2)',
              marginTop: 2,
              fontWeight: 600,
            }}
          >
            {yes ? 'Would date' : "Wouldn't date"}
          </div>
        </div>
        {hasNote && (
          <button
            onClick={onShare}
            aria-label="Share note"
            style={{
              width: 32,
              height: 32,
              borderRadius: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--ink-3)',
              background: 'var(--chip)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Icons.share size={13} />
          </button>
        )}
      </div>
      {hasNote && (
        <div className="serif" style={{ fontSize: 16, lineHeight: 1.45, marginTop: 14 }}>
          “{r.note}”
        </div>
      )}
    </div>
  );
}

function relativeWhen(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
